import S, { DataSignal } from 's-js';
import { filterWrites } from 'lib/filterSignalWrites';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';

/**
 * A datetime value whose date and time portions have been split into separate signals.
 */
export interface SplitDateTimePortions {
    /**
     * The signal's date portion. No guarantees of any sort are made about the time and offset portions of this
     * value; they should be ignored.
     */
     DatePortion : DataSignal<TZDate | null>;

     /**
      * The signal's time portion. No guarantees of any sort are made about the date and offset portions of this
      * value; they should be ignored.
      */
     TimePortion : DataSignal<TZDate | null>;
}

/**
 * A datetime-valued signal whose date and time portions can be initialized separately, say via separate date
 * and time picker components. The combined signal won't be initialized until both its date and time portions
 * have been initialized. Read "ProgInit" as "progressively initialized."
 */
export type ProgInitDateTime = DataSignal<TZDate | null> & SplitDateTimePortions;

const
    allowNonNull = <T>(val : T | null) => val !== null,
    allowAny = () => true;

/**
 * A datetime-valued signal whose date and time portions can be initialized separately, say via separate date
 * and time picker components. The combined signal won't be initialized until both its date and time portions
 * have been initialized. Read "ProgInit" as "progressively initialized."
 */
export function ProgInitDateTime(
    seed : TZDate | null,
    tzutils : TZUtils,
    allowClearing = false
) : ProgInitDateTime {
    const
        DatePortion = filterWrites(S.value(seed, TZDate.nullableEq), allowClearing ? allowAny : allowNonNull),
        TimePortion = filterWrites(S.value(seed, TZDate.nullableEq), allowClearing ? allowAny : allowNonNull),
        merged = S(() => mergeDateAndTime(DatePortion(), TimePortion(), tzutils)),
        Value = (val? : TZDate | null) =>
            val === undefined ? merged() :
            val === null && !allowClearing ? S.sample(merged) :
            S.freeze(() => (DatePortion(val), TimePortion(val)));

    Value.DatePortion = DatePortion;
    Value.TimePortion = TimePortion;

    return Value;
}

function mergeDateAndTime(datePortion : TZDate | null, timePortion : TZDate | null, tzutils : TZUtils) {
    if (datePortion === null || timePortion === null) return null;

    const val = new TZDate(0);

    // Our usual trick for setting month and date together. See e.g. tzutils.tzify method.
    val.setDate(1);
    val.setFullYear    (datePortion.getFullYear    ());
    val.setMonth       (datePortion.getMonth       ());
    val.setDate        (datePortion.getDate        ());
    val.setHours       (timePortion.getHours       ());
    val.setMinutes     (timePortion.getMinutes     ());
    val.setSeconds     (timePortion.getSeconds     ());
    val.setMilliseconds(timePortion.getMilliseconds());

    // IMPORTANT: Set timezone offset when done, making sure to preserve walltime.
    tzutils.setTimezoneOffset(val, true);

    return val;
}



// WEBPACK FOOTER //
// ./apps/portcall/datetimepicker/progInit.ts